import React, { useState, useEffect } from 'react';
import { User as UserIcon, Lock, CheckCircle2, AlertTriangle, Mail, Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Auth() {
  const { t, language, setLanguage } = useLanguage();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [registeredUser, setRegisteredUser] = useState(null);
  const [demoRecoveryCode, setDemoRecoveryCode] = useState(null);
  const [emailForRecovery, setEmailForRecovery] = useState('');

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/hub', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const savedUsername = localStorage.getItem('rememberedUsername');
    if (savedUsername) {
      setUsername(savedUsername);
      setRememberMe(true);
    }
  }, []);

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setSuccessMessage('');
    setShowPassword(false);
    setDemoRecoveryCode(null);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    // App uses HashRouter: redirect must include /#/ so the returned
    // #session_id fragment is parsed as the route's sub-hash by react-router
    const redirectUrl = window.location.origin + '/#/hub';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setDemoRecoveryCode(null);
    setLoading(true);

    try {
      if (mode === 'login') {
        const res = await fetch(`${API_URL}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password })
        });
        const data = await res.json();
        if (res.ok) {
          if (rememberMe) {
            localStorage.setItem('rememberedUsername', username);
          } else {
            localStorage.removeItem('rememberedUsername');
          }
          login(data, data.token);
          // Redirect to hub instead of direct to dashboard
          navigate('/hub');
        } else {
          setError(data.detail || data.error || t.auth.errorLogin);
        }
      } else if (mode === 'register') {
        const res = await fetch(`${API_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, email, password })
        });
        const data = await res.json();
        if (res.ok) {
          setRegisteredUser(data);
          setMode('registered');
        } else {
          setError(data.detail || data.error || t.auth.errorRegister);
        }
      } else if (mode === 'reset') {
        // STEP 2: Reset password with code
        
        // Validate passwords match
        if (newPassword !== confirmPassword) {
          setError('❌ Las contraseñas no coinciden. Por favor verifica que sean iguales.');
          setLoading(false);
          return;
        }
        
        // Validate password length
        if (newPassword.length < 6) {
          setError('❌ La contraseña debe tener al menos 6 caracteres.');
          setLoading(false);
          return;
        }
        
        const res = await fetch(`${API_URL}/api/auth/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            username: emailForRecovery || username.includes('@') ? undefined : username, 
            email: emailForRecovery || username.includes('@') ? (emailForRecovery || username) : undefined, 
            recovery_code: recoveryCode.trim().toUpperCase(), 
            new_password: newPassword 
          })
        });
        const data = await res.json();
        if (res.ok) {
          setSuccessMessage('✅ Contraseña restablecida exitosamente. Ahora puedes iniciar sesión con tu nueva contraseña.');
          setTimeout(() => {
            setMode('login');
            setPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setRecoveryCode('');
            setEmailForRecovery('');
            setSuccessMessage('');
          }, 3000);
        } else {
          setError(data.detail || data.error || '❌ Código inválido o expirado. Por favor intenta nuevamente.');
        }
      } else if (mode === 'forgot-code') {
        // STEP 1: Request recovery code (only ask for email)
        const res = await fetch(`${API_URL}/api/auth/recover-code`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: emailForRecovery || email })
        });
        const data = await res.json();
        if (res.ok) {
          setSuccessMessage('✅ Código enviado a tu email. Revisa tu bandeja de entrada y spam.');
          setEmailForRecovery(emailForRecovery || email);
          // Auto-switch to reset mode
          setTimeout(() => {
            setMode('reset');
            setSuccessMessage('');
          }, 2000);
          if (data.demoCode) {
            setDemoRecoveryCode(data.demoCode);
          }
        } else {
          // Always show success for security (don't reveal if email exists)
          setSuccessMessage('✅ Si el email está registrado, recibirás las instrucciones en tu correo.');
          setEmailForRecovery(emailForRecovery || email);
          setTimeout(() => {
            setMode('reset');
            setSuccessMessage('');
          }, 2000);
        }
      } else if (mode === 'forgot-username') {
        // Request username reminder via email
        const res = await fetch(`${API_URL}/api/auth/recover-username`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: emailForRecovery || email })
        });
        const data = await res.json();
        if (res.ok) {
          setSuccessMessage('✅ Tu usuario fue enviado a tu email. Revisa tu bandeja de entrada.');
          setTimeout(() => {
            setMode('login');
            setSuccessMessage('');
          }, 3000);
        } else {
          setSuccessMessage('✅ Si el email está registrado, recibirás tu usuario en tu correo.');
          setTimeout(() => {
            setMode('login');
            setSuccessMessage('');
          }, 3000);
        }
      }
    } catch (err) {
      setError(t.auth.errorConnection);
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'registered' && registeredUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 space-y-6 border border-gray-100 text-center">
          <div className="inline-flex p-3 bg-green-50 rounded-full text-green-600 mb-2">
            <CheckCircle2 size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">{t.auth.accountCreated}</h2>
          <p className="text-gray-600">
            {t.auth.saveRecoveryCode}
          </p>
          <div className="bg-gray-100 p-4 rounded-xl font-mono text-xl tracking-widest font-bold text-center select-all text-blue-900 border border-gray-200" data-testid="recovery-code">
            {registeredUser.recoveryCode}
          </div>
          <button 
            onClick={() => {
              login(registeredUser);
              navigate('/dashboard');
            }}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl shadow-lg shadow-blue-200 transition-all transform active:scale-95"
            data-testid="continue-to-app-btn"
          >
            {t.auth.continueToApp}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 relative">
      <div className="absolute top-4 right-4">
        <button
          onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-white hover:bg-gray-100 transition-all shadow-sm border border-gray-200 overflow-hidden active:scale-95 group"
          title={language === 'es' ? 'Switch to English' : 'Cambiar a Español'}
          data-testid="auth-language-toggle"
        >
          <img 
            src={language === 'es' ? "https://flagcdn.com/w40/us.png" : "https://flagcdn.com/w40/es.png"} 
            alt={language === 'es' ? "English" : "Español"}
            className="w-full h-full object-cover rounded-full group-hover:scale-110 transition-transform"
            referrerPolicy="no-referrer"
          />
        </button>
      </div>
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 space-y-6 border border-gray-100">
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-black text-blue-900 tracking-tight">{t.auth.title}</h2>
          <p className="text-gray-500">
            {mode === 'login' ? t.auth.login : 
             mode === 'register' ? t.auth.register : 
             mode === 'forgot-code' ? 'Ingresa tu email para recuperar tu contraseña' :
             mode === 'forgot-username' ? 'Ingresa tu email para recuperar tu usuario' :
             mode === 'reset' ? 'Crea tu nueva contraseña' :
             t.auth.resetPassword}
          </p>
        </div>

        {mode === 'login' && (
          <>
            <button
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700 font-bold py-3 rounded-xl transition-all transform active:scale-95"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t.auth.googleLogin}
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">{t.auth.or}</span>
              </div>
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {(mode === 'login' || mode === 'register') && (
            <div className="space-y-1">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                {t.auth.username}
              </label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  placeholder={t.auth.usernamePlaceholder}
                  data-testid="username-input"
                />
              </div>
            </div>
          )}

          {(mode === 'register' || mode === 'forgot-code' || mode === 'forgot-username') && (
            <div className="space-y-1">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                {mode === 'forgot-code' || mode === 'forgot-username' ? 'EMAIL' : t.auth.email}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="email"
                  required
                  value={mode === 'forgot-code' || mode === 'forgot-username' ? (emailForRecovery || email) : email}
                  onChange={(e) => {
                    if (mode === 'forgot-code' || mode === 'forgot-username') {
                      setEmailForRecovery(e.target.value);
                    } else {
                      setEmail(e.target.value);
                    }
                  }}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                  placeholder={mode === 'forgot-code' || mode === 'forgot-username' ? 'tu@email.com' : t.auth.emailPlaceholder}
                  data-testid="email-input"
                />
              </div>
            </div>
          )}

          {mode === 'reset' && (
            <div className="space-y-1">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">{t.auth.recoveryCode}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  required
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all uppercase"
                  placeholder={t.auth.recoveryCodePlaceholder}
                  data-testid="recovery-code-input"
                />
              </div>
            </div>
          )}

          {mode !== 'forgot-code' && mode !== 'forgot-username' && (
            <>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  {mode === 'reset' ? 'NUEVA CONTRASEÑA' : t.auth.password}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={mode === 'reset' ? newPassword : password}
                    onChange={(e) => mode === 'reset' ? setNewPassword(e.target.value) : setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    placeholder={mode === 'reset' ? 'Mínimo 6 caracteres' : t.auth.passwordPlaceholder}
                    minLength={mode === 'reset' ? 6 : undefined}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              {mode === 'reset' && (
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    CONFIRMAR NUEVA CONTRASEÑA
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                      placeholder="Escribe la contraseña nuevamente"
                      minLength={6}
                      data-testid="confirm-password-input"
                    />
                  </div>
                  {confirmPassword && newPassword && confirmPassword !== newPassword && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <span>⚠️</span> Las contraseñas no coinciden
                    </p>
                  )}
                  {confirmPassword && newPassword && confirmPassword === newPassword && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <span>✓</span> Las contraseñas coinciden
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {mode === 'login' && (
            <div className="flex items-center gap-2 py-1">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
              />
              <label htmlFor="rememberMe" className="text-sm font-medium text-gray-600 cursor-pointer select-none">
                {t.auth.rememberMe}
              </label>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2 border border-red-100" data-testid="auth-error">
              <AlertTriangle size={16} />
              {error}
            </div>
          )}

          {successMessage && (
            <div className="p-3 bg-green-50 text-green-600 text-sm rounded-lg flex flex-col gap-2 border border-green-100" data-testid="auth-success">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={16} />
                {successMessage}
              </div>
              {demoRecoveryCode && (
                <div className="mt-2 p-3 bg-white border border-green-200 rounded-xl">
                  <p className="text-xs text-green-700 mb-1 font-bold uppercase tracking-wider text-center">
                    {language === 'es' ? 'Modo Demo: Tu Código' : 'Demo Mode: Your Code'}
                  </p>
                  <div className="font-mono text-center text-blue-900 font-black text-xl tracking-widest">
                    {demoRecoveryCode}
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || (mode === 'reset' && confirmPassword && newPassword !== confirmPassword)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl shadow-lg shadow-blue-200 transition-all transform active:scale-95 disabled:opacity-50 disabled:active:scale-100"
            data-testid="submit-btn"
          >
            {loading ? t.auth.loading : 
             mode === 'login' ? t.auth.loginBtn : 
             mode === 'register' ? t.auth.registerBtn : 
             mode === 'forgot-code' ? '📧 Enviar Código' :
             mode === 'forgot-username' ? '📧 Enviar Usuario' :
             mode === 'reset' ? '🔐 Restablecer Contraseña' :
             t.auth.resetBtn}
          </button>
        </form>

        <div className="text-center space-y-2">
          {mode === 'login' && (
            <>
              <button
                onClick={() => switchMode('register')}
                className="block w-full text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                data-testid="register-link"
              >
                {t.auth.noAccount}
              </button>
              <button
                onClick={() => switchMode('forgot-code')}
                className="block w-full text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                data-testid="forgot-password-link"
              >
                {t.auth.forgotPassword}
              </button>
              <button
                onClick={() => switchMode('forgot-username')}
                className="block w-full text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                data-testid="forgot-username-link"
              >
                ¿Olvidaste tu usuario?
              </button>
            </>
          )}

          {mode === 'reset' && (
            <button
              onClick={() => switchMode('forgot-code')}
              className="block w-full text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
            >
              {t.auth.forgotRecoveryCode}
            </button>
          )}
          
          {(mode === 'register' || mode === 'reset' || mode === 'forgot-code' || mode === 'forgot-username') && (
            <button
              onClick={() => switchMode('login')}
              className="block w-full text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
              data-testid="back-to-login-link"
            >
              {t.auth.backToLogin}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
